import { z } from "zod";

const projectSnapshotSchema = z.object({
  id: z.string(),
  syncToken: z.string(),
  clientCreatedAtTimestamp: z.number(),
  serverCreatedAtTimestamp: z.number(),
  isCurrent: z.boolean(),
});

const projectStatsSchema = z.object({
  durationMs: z.number().nullable(),
  aspectRatio: z
    .object({
      width: z.number(),
      height: z.number(),
      label: z.string(),
    })
    .nullable(),
  shotsCount: z.number(),
  captionsCount: z.number(),
  overlaysCount: z.number(),
  audioCount: z.number(),
});

const projectShotSchema = z.object({
  id: z.string(),
  index: z.number(),
  startMs: z.number(),
  endMs: z.number(),
  durationMs: z.number(),
  assetId: z.string().nullable(),
});

export const projectPlatformFilterSchema = z.enum(["ios", "web"]);
export const projectCreationSourceFilterSchema = z.enum(["basecamp"]);

const projectSummarySchema = z.object({
  isDeleted: z.boolean().optional(),
  id: z.string(),
  optimisticId: z.string().nullable(),
  createdBy: z.string().nullable(),
  createdByProductId: z.string().nullable(),
  creationSource: z.string().nullish(),
  title: z.string().nullable(),
  editedAtTimestamp: z.number(),
  serverCreatedAtTimestamp: z.number().nullable(),
  syncToken: z.string().nullable(),
  dataModelId: z.string().nullable(),
  thumbnailUrl: z.string().nullable(),
  thumbnailAssetId: z.string().nullable(),
  hasDataModel: z.boolean(),
});

const projectDetailSchema = projectSummarySchema.extend({
  snapshots: z.array(projectSnapshotSchema),
  currentSnapshotId: z.string().nullable(),
  snapshotCount: z.number(),
});

const projectSnapshotPlaybackSchema = z.object({
  project: projectSummarySchema,
  snapshot: projectSnapshotSchema,
  protoUri: z.string(),
  projectDataUrl: z.string(),
  projectJson: z.unknown(),
  assetUrlMap: z.record(z.string()),
  assetUrlMapLow: z.record(z.string()).optional(),
  stats: projectStatsSchema,
  shots: z.array(projectShotSchema),
});

const projectVersionSyncTypeSchema = z.enum([
  "actions",
  "assetData",
  "undo",
  "redo",
  "fullProject",
  "unknown",
]);

export const projectVersionActionsResponseSchema = z.object({
  projectId: z.string(),
  syncToken: z.string(),
  syncType: projectVersionSyncTypeSchema,
  actions: z.array(z.unknown()).default([]),
  serverCommitId: z.string().nullish(),
  clientMutationId: z.string().nullish(),
  sourceOperationId: z.string().nullish(),
  baseSyncToken: z.string().nullish(),
  restoredSyncToken: z.string().nullish(),
  referencedClientMutationId: z.string().nullish(),
  acceptedAt: z.string().nullish(),
});

const editorProjectDetailsCreationMetadataSchema = z.object({
  productType: z.string().nullish(),
});

const editorProjectDetailsSchema = z.object({
  id: z.string(),
  title: z.string().nullish(),
  projectDataUrl: z.string().url(),
  assetUrlMap: z.record(z.string()),
  assetUrlMapLow: z.record(z.string()).optional(),
  assetMetadataMap: z.record(z.unknown()).nullish(),
  mirageStudioProjectId: z.string().nullish(),
  mirageStudioVideoId: z.string().nullish(),
  projectGroupId: z.string().nullish(),
  syncToken: z.string(),
  creationMetadata: editorProjectDetailsCreationMetadataSchema.nullish(),
});

const projectChatThreadSchema = z.object({
  threadId: z.string(),
  scope: z.string(),
  projectId: z.string().nullish(),
  title: z.string().nullish(),
  activeOperationId: z.string().nullish(),
  createdByUserId: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const projectChatClientContextSchema = z
  .object({
    projectSyncToken: z.string().nullish(),
    playback: z.unknown().nullish(),
    attachments: z.array(z.unknown()).nullish(),
    selections: z.array(z.unknown()).nullish(),
  })
  .passthrough();

const projectChatMessagePartSchema = z
  .object({
    type: z.string(),
    partId: z.string(),
    text: z.string().optional(),
    toolName: z.string().optional(),
    toolCallId: z.string().nullish(),
    input: z.record(z.unknown()).optional(),
    output: z.record(z.unknown()).nullish(),
    title: z.string().optional(),
    proposal: z
      .object({
        sourceOperationId: z.string().optional(),
        mutationSchemaVersion: z.string().optional(),
        actions: z.array(z.unknown()).optional(),
      })
      .passthrough()
      .optional(),
    assets: z.array(z.unknown()).nullish(),
    committed: z.boolean().nullish(),
  })
  .passthrough();

const projectChatMessageSchema = z.object({
  messageId: z.string(),
  threadId: z.string(),
  role: z.enum(["user", "assistant"]),
  state: z.string(),
  content: z.string().default(""),
  parts: z.array(projectChatMessagePartSchema).default([]),
  operationId: z.string().nullish(),
  clientContext: projectChatClientContextSchema.nullish(),
  turnClientContext: projectChatClientContextSchema.nullish(),
  debug: z
    .object({
      durationMs: z.number().nullish(),
      billedCredits: z.number().nullish(),
      totalCredits: z.number().nullish(),
    })
    .nullish(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const projectChatMutationCommitSchema = z.object({
  projectId: z.string(),
  sourceOperationId: z.string(),
  syncToken: z.string(),
  backend: z.enum(["legacy", "object_sync"]).optional().default("legacy"),
  serverCommitId: z.string().nullish(),
  clientSessionId: z.string().nullish(),
  clientMutationId: z.string().nullish(),
  clientSequence: z.number().nullish(),
  actionId: z.string().nullish(),
});

const projectChatThreadSnapshotSchema = z.object({
  thread: projectChatThreadSchema,
  messages: z.array(projectChatMessageSchema),
  mutationCommits: z.array(projectChatMutationCommitSchema),
});

export const projectFeedResponseSchema = z.object({
  projects: z.object({
    page: z.array(projectSummarySchema),
    pageInfo: z.object({
      hasNextPage: z.boolean(),
      endCursor: z.string().nullable(),
    }),
  }),
});

export const projectDetailResponseSchema = z.object({
  project: projectDetailSchema,
});

export const projectSnapshotPlaybackResponseSchema = z.object({
  playback: projectSnapshotPlaybackSchema,
});

export const editorProjectDetailsResponseSchema = editorProjectDetailsSchema;

export const projectChatThreadsResponseSchema = z.object({
  threads: z.array(projectChatThreadSnapshotSchema),
});

export const projectChatFeedResponseSchema = z.object({
  threads: z.object({
    page: z.array(
      z.object({
        thread: projectChatThreadSchema,
        isTerminal: z.boolean().optional().default(false),
        latestActivityAt: z.string(),
        messageCount: z.number(),
        mutationCount: z.number(),
        committedMutationCount: z.number(),
        previews: z.object({ user: z.string(), assistant: z.string() }),
        latestTurn: z
          .object({
            creditsUsed: z.number().nullish(),
            creditsAreApproximate: z.boolean().optional(),
            cancelled: z.boolean(),
            latencySeconds: z.number().nullish(),
          })
          .nullish(),
      })
    ),
    pageInfo: z.object({
      hasNextPage: z.boolean(),
      endCursor: z.string().nullable().optional(),
    }),
  }),
});

export const failedGenerationItemSchema = z.object({
  mirageStudioProjectId: z.string(),
  mirageStudioVideoId: z.string(),
  captionsProjectId: z.string().nullish(),
  videoType: z.string().nullish(),
  productType: z.string().nullish(),
  errorCode: z.string().nullish(),
  error: z.string().nullish(),
  workflowId: z.string().nullish(),
  updatedAtMs: z.number().nullish(),
});

export const failedGenerationsResponseSchema = z.object({
  items: z.array(failedGenerationItemSchema),
});

export const projectOverviewStepSchema = z.object({
  name: z.string(),
  startedAtMs: z.number().nullish(),
  endedAtMs: z.number().nullish(),
  durationMs: z.number().nullish(),
  approximate: z.boolean().optional().default(false),
  count: z.number().nullish(),
  failed: z.boolean().optional().default(false),
});

export const projectOverviewGapSchema = z.object({
  startedAtMs: z.number(),
  endedAtMs: z.number(),
  durationMs: z.number(),
  beforeEvent: z.string().nullish(),
  afterEvent: z.string().nullish(),
});

export const projectOverviewFailureSchema = z.object({
  type: z.string(),
  message: z.string().nullish(),
  temporalEventId: z.number().nullish(),
});

export const projectOverviewResponseSchema = z.object({
  captionsProjectId: z.string(),
  productType: z.string().nullish(),
  videoType: z.string().nullish(),
  videoStatus: z.string().nullish(),
  mirageStudioProjectId: z.string().nullish(),
  mirageStudioVideoId: z.string().nullish(),
  morcVideoId: z.string().nullish(),
  workflowId: z.string().nullish(),
  videoDurationMs: z.number().nullish(),
  steps: z.array(projectOverviewStepSchema).optional().default([]),
  gaps: z.array(projectOverviewGapSchema).optional().default([]),
  failure: projectOverviewFailureSchema.nullish(),
  totalDurationMs: z.number().nullish(),
  totalDurationApproximate: z.boolean().optional().default(false),
});

export const projectSearchProductTypeSchema = z.enum([
  "REAL_ESTATE",
  "AI_EDIT",
  "MUSIC",
  "LONG_TO_SHORT",
  "VOICEOVER",
  "AVATAR",
  "PRODUCT_AD",
]);

export const projectSearchProductTypes = projectSearchProductTypeSchema.options;

export const projectSearchEditStateSchema = z.enum([
  "QUEUED",
  "PROCESSING",
  "COMPLETE",
  "FAILED",
  "CANCELED",
]);

export const projectSearchItemSchema = z.object({
  projectId: z.string(),
  videoId: z.string(),
  captionsProjectId: z.string().nullish(),
  renderedProjectId: z.string().nullish(),
  projectTitle: z.string().nullish(),
  productType: projectSearchProductTypeSchema,
  editState: projectSearchEditStateSchema,
  operationId: z.string().nullish(),
  workflowId: z.string().nullish(),
  renderedVideoAssetId: z.string().nullish(),
  errorCode: z.string().nullish(),
  error: z.string().nullish(),
  createdAt: z.string().nullish(),
  updatedAt: z.string().nullish(),
});

export const projectSearchResponseSchema = z.object({
  items: z.array(projectSearchItemSchema),
});

export type ProjectSummary = z.infer<typeof projectSummarySchema>;
export type ProjectSnapshot = z.infer<typeof projectSnapshotSchema>;
export type ProjectShot = z.infer<typeof projectShotSchema>;
export type ProjectSnapshotPlayback = z.infer<typeof projectSnapshotPlaybackSchema>;
export type ProjectVersionActions = z.infer<typeof projectVersionActionsResponseSchema>;
export type EditorProjectDetails = z.infer<typeof editorProjectDetailsSchema>;
export type ProjectChatMessage = z.infer<typeof projectChatMessageSchema>;
export type ProjectChatMessagePart = z.infer<typeof projectChatMessagePartSchema>;
export type ProjectChatMutationCommit = z.infer<typeof projectChatMutationCommitSchema>;
export type ProjectChatThreadSnapshot = z.infer<typeof projectChatThreadSnapshotSchema>;
export type ProjectChatFeedResponse = z.infer<typeof projectChatFeedResponseSchema>;
export type ProjectChatFeedItem = ProjectChatFeedResponse["threads"]["page"][number];
export type FailedGenerationItem = z.infer<typeof failedGenerationItemSchema>;
export type ProjectOverview = z.infer<typeof projectOverviewResponseSchema>;
export type ProjectOverviewStep = z.infer<typeof projectOverviewStepSchema>;
export type ProjectSearchProductType = z.infer<typeof projectSearchProductTypeSchema>;
export type ProjectSearchEditState = z.infer<typeof projectSearchEditStateSchema>;
export type ProjectSearchItem = z.infer<typeof projectSearchItemSchema>;
